import React from 'react';
import styled from 'styled-components';
import useLocale from '../../hooks/useLocale';
import { isQ3Enabled } from './utils/featureFlags';

const Section = styled.section`
  max-width: 560px;
  margin: ${p => p.theme.spacing[4]}px auto 0;
  padding: ${p => p.theme.spacing[4]}px;
  background: rgba(255, 251, 240, 0.6);
  border: 1px solid rgba(218, 165, 32, 0.25);
  border-radius: 12px;
`;

const Heading = styled.h3`
  font-family: 'Noto Sans Tamil', 'Playfair Display', serif;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #8B6914;
  margin: 0 0 ${p => p.theme.spacing[3]}px;
  text-align: center;
`;

const FieldRow = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: ${p => p.theme.spacing[3]}px;
  row-gap: ${p => p.theme.spacing[2]}px;
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};

  dt {
    color: ${p => p.theme.colors.text.secondary};
    font-weight: 500;
  }
  dd {
    margin: 0;
    color: ${p => p.theme.colors.text.primary};
  }
`;

function pickName(field, lang) {
  if (!field) return '';
  return lang === 'ta' ? (field.name_ta || field.name_en || '') : (field.name_en || field.name_ta || '');
}

function pickPlanet(field, lang) {
  if (!field) return '';
  return lang === 'ta' ? (field.planet_ta || field.planet_en || '') : (field.planet_en || field.planet_ta || '');
}

function nakshatraDisplay(nak, lang) {
  if (!nak) return '';
  const name = pickName(nak, lang);
  const lord = lang === 'ta' ? nak.lord_ta : nak.lord_en;
  return lord ? `${name} (${lord})` : name;
}

function tithiDisplay(tithi, t, lang) {
  if (!tithi) return '';
  const name = pickName(tithi, lang);
  if (!tithi.paksha) return name;
  const paksha = tithi.paksha === 'krishna' ? t.pakshaKrishna : t.pakshaShukla;
  return `${paksha} ${name}`;
}

function horaDisplay(hora, t, lang) {
  if (!hora) return '';
  const planet = pickPlanet(hora, lang);
  return planet ? `${planet} ${t.horaSuffix || 'Hora'}` : '';
}

function yogamDisplay(yogam, lang) {
  if (!yogam) return '';
  return lang === 'ta'
    ? yogam.primary_ta || yogam.primary_en || ''
    : yogam.primary_en || yogam.primary_ta || '';
}

function activePeriodDisplay(snapshot, t) {
  const a = (snapshot && snapshot.active_periods) || {};
  const labels = [];
  if (a.nalla_neram) labels.push(t.activePeriodNallaNeram);
  if (a.rahu_kaal) labels.push(t.activePeriodRahuKaal);
  if (a.yamagandam) labels.push(t.activePeriodYamagandam);
  if (a.gulikai) labels.push(t.activePeriodGulikai);
  if (a.varjyam) labels.push(t.activePeriodVarjyam);
  if (a.bhadra) labels.push(t.activePeriodBhadra);
  if (a.panchaka) labels.push(t.activePeriodPanchaka);
  if (a.sarvartha_siddhi) labels.push(t.activePeriodSarvarthaSiddhi);
  if (a.amrit_siddhi) labels.push(t.activePeriodAmritSiddhi);
  return labels.length > 0 ? labels.join(', ') : t.activePeriodNone;
}

const PanchangaSnapshot = ({ snapshot, lang = 'en' }) => {
  const locale = useLocale(lang, 'prashnavali');
  const t = (locale && locale.panchangam) || {};

  if (!isQ3Enabled() || !snapshot) return null;

  return (
    <Section aria-label={t.snapshotHeading}>
      <Heading>{t.snapshotHeading}</Heading>
      <FieldRow>
        <dt>{t.nakshatra}</dt>
        <dd>{nakshatraDisplay(snapshot.nakshatra, lang)}</dd>
        <dt>{t.tithi}</dt>
        <dd>{tithiDisplay(snapshot.tithi, t, lang)}</dd>
        <dt>{t.hora}</dt>
        <dd>{horaDisplay(snapshot.hora, t, lang)}</dd>
        <dt>{t.tamilYogam}</dt>
        <dd>{yogamDisplay(snapshot.tamil_yogam, lang)}</dd>
        <dt>{t.activePeriod}</dt>
        <dd>{activePeriodDisplay(snapshot, t)}</dd>
      </FieldRow>
    </Section>
  );
};

export default PanchangaSnapshot;
